import {
  BlockColors,
  BlockShapes,
  type BlockConfiguration,
} from '@/types/blocks';
import {
  DEFAULT_CIRCLE_PROBABILITY,
  DEFAULT_QUARTER_CIRCLE_PROBABILITY,
  DEFAULT_RECTANGLE_PROBABILITY,
  DEFAULT_COLOR_VARIETY,
  DEFAULT_SAME_COLOR_ADJACENCY_WEIGHT,
} from '@/constants/blockGrid';
import { useMemo } from 'react';

// Seeded random number generator
const createRandom = (seed: number) => {
  let currentSeed = seed;
  return () => {
    // Simple LCG random number generator
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    return currentSeed / 233280;
  };
};

// Helper to get a random integer between 0 and max-1
function getRandomInt(random: () => number, max: number): number {
  return Math.floor(random() * max);
}

// Helper to get a random item from an array
function getRandomItem<T>(random: () => number, array: readonly T[]): T {
  return array[getRandomInt(random, array.length)];
}

// Generate a unique seed for each cell
function generateCellSeed(baseSeed: number, row: number, col: number): number {
  // Use prime numbers to mix the seed with position
  return ((baseSeed + 1) * 17 + row * 37 + col * 97) % 233280;
}

/*
rules:

- only one black circle in total within the grid
- only 2 adjacent 'square' blocks at maximum
- look for opportunity to have two quarter circles adjacent and rotated to form an apparent half circle
  - i.e. two quarter circles, one of type 'top left' and then 'top right' next to each other
  - half circles can be vertical or horizontal
  - these should have matching black colors to form a visual half circle
- other shapes cannot be black except for quarter circles forming a half circle. only one circle can be black, no other black should appear.
*/

export type BlockGridParameters = {
  width: number;
  height: number;
  seed: number;
  // Probabilities for different block types (0-1)
  circleProbability?: number;
  quarterCircleProbability?: number;
  rectangleProbability?: number;
  // Number of colors to use (1-6)
  colorVariety?: number;
  // Weight for adjacent blocks to have the same color (0-1)
  sameColorAdjacencyWeight?: number;
};

export const useBlockGrid = ({
  width,
  height,
  seed,
  circleProbability = DEFAULT_CIRCLE_PROBABILITY,
  quarterCircleProbability = DEFAULT_QUARTER_CIRCLE_PROBABILITY,
  rectangleProbability = DEFAULT_RECTANGLE_PROBABILITY,
  colorVariety = DEFAULT_COLOR_VARIETY,
  sameColorAdjacencyWeight = DEFAULT_SAME_COLOR_ADJACENCY_WEIGHT,
}: BlockGridParameters) => {
  // Helper to check if two quarter circles can form a half circle
  // This function is more strict about the relative positions of the quarter circles
  function canFormHalfCircle(
    shape1: BlockConfiguration['shape'],
    shape2: BlockConfiguration['shape'],
    position: 'right' | 'left' | 'below' | 'above',
  ): boolean {
    if (
      shape1.shape !== 'quarter-circle' ||
      shape2.shape !== 'quarter-circle'
    ) {
      return false;
    }

    // Get the orientations
    const orientation1 = shape1.orientation;
    const orientation2 = shape2.orientation;

    // Check based on the relative position
    if (position === 'right') {
      // shape2 is to the right of shape1
      return (
        (orientation1 === 'top-left' && orientation2 === 'top-right') ||
        (orientation1 === 'bottom-left' && orientation2 === 'bottom-right')
      );
    } else if (position === 'left') {
      // shape2 is to the left of shape1
      return (
        (orientation1 === 'top-right' && orientation2 === 'top-left') ||
        (orientation1 === 'bottom-right' && orientation2 === 'bottom-left')
      );
    } else if (position === 'below') {
      // shape2 is below shape1
      return (
        (orientation1 === 'top-left' && orientation2 === 'bottom-left') ||
        (orientation1 === 'top-right' && orientation2 === 'bottom-right')
      );
    } else if (position === 'above') {
      // shape2 is above shape1
      return (
        (orientation1 === 'bottom-left' && orientation2 === 'top-left') ||
        (orientation1 === 'bottom-right' && orientation2 === 'top-right')
      );
    }

    return false;
  }

  // Count adjacent squares in the grid
  function countAdjacentSquares(
    grid: BlockConfiguration[][],
    row: number,
    col: number,
  ): number {
    let count = 0;
    const directions = [
      [-1, 0], // up
      [1, 0], // down
      [0, -1], // left
      [0, 1], // right
    ];

    for (const [dx, dy] of directions) {
      const newRow = row + dx;
      const newCol = col + dy;

      if (
        newRow >= 0 &&
        newRow < grid.length &&
        newCol >= 0 &&
        newCol < grid[0].length &&
        grid[newRow][newCol].shape.shape === 'square'
      ) {
        count++;
      }
    }

    return count;
  }

  // Ensure probabilities are valid
  const normalizedCircleProb = Math.max(0, Math.min(1, circleProbability));
  const normalizedQuarterCircleProb = Math.max(
    0,
    Math.min(1, quarterCircleProbability),
  );
  const normalizedRectangleProb = Math.max(
    0,
    Math.min(1, rectangleProbability),
  );

  // Normalize probabilities to sum to 1
  const totalProb =
    normalizedCircleProb +
    normalizedQuarterCircleProb +
    normalizedRectangleProb;

  const circleProb = totalProb > 0 ? normalizedCircleProb / totalProb : 0.33;
  const quarterCircleProb =
    totalProb > 0 ? normalizedQuarterCircleProb / totalProb : 0.33;
  const rectangleProb =
    totalProb > 0 ? normalizedRectangleProb / totalProb : 0.34;

  // Get black color from BlockColors for type safety
  const blackColor = BlockColors[0]; // 'black' is the first color

  // Handle colors, ensuring black is always available for circles
  // but other colors are limited by colorVariety
  const nonBlackColors = BlockColors.filter((color) => color !== blackColor);
  const limitedColorVariety = Math.max(
    1,
    Math.min(nonBlackColors.length, colorVariety),
  );
  const availableNonBlackColors = nonBlackColors.slice(0, limitedColorVariety);

  // Helper to get a weighted random shape based on probabilities
  function getRandomShape(random: () => number): (typeof BlockShapes)[number] {
    const r = random();

    if (r < circleProb) {
      return { shape: 'circle' };
    } else if (r < circleProb + quarterCircleProb) {
      // Choose a random quarter circle orientation
      const quarterCircleShapes = BlockShapes.filter(
        (shape) => shape.shape === 'quarter-circle',
      ).sort(() => random() - 0.5);
      return getRandomItem(random, quarterCircleShapes);
    } else {
      // Rectangle (square)
      return { shape: 'square' };
    }
  }

  // Apply rules to the grid
  function applyRules(
    grid: BlockConfiguration[][],
    random: () => number,
  ): BlockConfiguration[][] {
    const height = grid.length;
    const width = grid[0].length;
    const newGrid = JSON.parse(JSON.stringify(grid)) as BlockConfiguration[][];

    // Find all potential half circle pairs
    const halfCirclePairs: Array<[number, number, number, number]> = [];

    // Find all squares with too many adjacent squares
    const squaresWithTooManyAdjacent: [number, number][] = [];

    // First pass: Analyze the grid to identify patterns and issues
    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        // Create a seeded random generator for this cell to decide between horizontal and vertical
        const cellSeed = generateCellSeed(seed + 2, row, col); // Use a different seed offset
        const cellRandom = createRandom(cellSeed);

        // Check for potential half circles in all four directions
        const possiblePairs: Array<[number, number, number, number]> = [];

        // Check right
        if (
          col + 1 < width &&
          canFormHalfCircle(
            grid[row][col].shape,
            grid[row][col + 1].shape,
            'right',
          )
        ) {
          possiblePairs.push([row, col, row, col + 1]);
        }

        // Check left
        if (
          col - 1 >= 0 &&
          canFormHalfCircle(
            grid[row][col].shape,
            grid[row][col - 1].shape,
            'left',
          )
        ) {
          possiblePairs.push([row, col, row, col - 1]);
        }

        // Check below
        if (
          row + 1 < height &&
          canFormHalfCircle(
            grid[row][col].shape,
            grid[row + 1][col].shape,
            'below',
          )
        ) {
          possiblePairs.push([row, col, row + 1, col]);
        }

        // Check above
        if (
          row - 1 >= 0 &&
          canFormHalfCircle(
            grid[row][col].shape,
            grid[row - 1][col].shape,
            'above',
          )
        ) {
          possiblePairs.push([row, col, row - 1, col]);
        }

        // If we found any possible pairs, randomly choose one
        if (possiblePairs.length > 0) {
          const chosenPairIndex = Math.floor(
            cellRandom() * possiblePairs.length,
          );
          halfCirclePairs.push(possiblePairs[chosenPairIndex]);
        }

        // Check for squares with too many adjacent squares
        if (grid[row][col].shape.shape === 'square') {
          const adjacentSquares = countAdjacentSquares(grid, row, col);
          if (adjacentSquares > 2) {
            squaresWithTooManyAdjacent.push([row, col]);
          }
        }
      }
    }

    // Second pass: Apply rules based on the analysis

    // Create a set to track positions that are part of half circles
    const halfCirclePositions = new Set<string>();

    // Track if we've already created a black semicircle
    let hasBlackSemicircle = false;

    // Rule 3: Create visual half circles with matching colors (apply this first)
    for (const [row1, col1, row2, col2] of halfCirclePairs) {
      // Skip if either block has been changed to a non-quarter-circle
      if (
        newGrid[row1][col1].shape.shape !== 'quarter-circle' ||
        newGrid[row2][col2].shape.shape !== 'quarter-circle'
      ) {
        continue;
      }

      // Determine the relative position of the second cell to the first
      let position: 'right' | 'left' | 'below' | 'above';
      if (col2 > col1) {
        position = 'right';
      } else if (col2 < col1) {
        position = 'left';
      } else if (row2 > row1) {
        position = 'below';
      } else {
        position = 'above';
      }

      // Check if they still form a half circle, and neither is black (a previous rule marked this a semi-circle in another direction)
      if (
        canFormHalfCircle(
          newGrid[row1][col1].shape,
          newGrid[row2][col2].shape,
          position,
        ) &&
        newGrid[row1][col1].color !== blackColor &&
        newGrid[row2][col2].color !== blackColor
      ) {
        // Choose a color for the semicircle
        let semicircleColor: (typeof BlockColors)[number];

        if (!hasBlackSemicircle && random() < 0.3) {
          // This will be our one black semicircle
          semicircleColor = blackColor;
          hasBlackSemicircle = true;
        } else {
          semicircleColor = newGrid[row1][col1].color;
        }

        // Apply the same color to both quarter circles
        newGrid[row1][col1].color = semicircleColor;
        newGrid[row2][col2].color = semicircleColor;

        // Mark these positions as part of a half circle
        halfCirclePositions.add(`${row1},${col1}`);
        halfCirclePositions.add(`${row2},${col2}`);
      }
    }

    // Rule 1: Ensure at most one black circle and no black non-circles (except half circles)
    let hasBlackCircle = false;

    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        const block = newGrid[row][col];
        const isPartOfHalfCircle = halfCirclePositions.has(`${row},${col}`);

        if (block.color === blackColor) {
          if (block.shape.shape === 'circle') {
            if (hasBlackCircle) {
              // Already have a black circle, change this one
              block.color = getRandomItem(random, availableNonBlackColors);
            } else {
              // This is our one black circle
              hasBlackCircle = true;
            }
          } else if (!isPartOfHalfCircle) {
            // Non-circle shapes can't be black unless they're part of a half circle
            block.color = getRandomItem(random, availableNonBlackColors);
          }
        }
      }
    }

    // If we don't have a black circle and there are circles, make one black
    if (!hasBlackCircle && circleProb > 0) {
      const circlePositions: [number, number][] = [];
      for (let row = 0; row < height; row++) {
        for (let col = 0; col < width; col++) {
          if (newGrid[row][col].shape.shape === 'circle') {
            circlePositions.push([row, col]);
          }
        }
      }

      if (circlePositions.length > 0) {
        const [row, col] =
          circlePositions[getRandomInt(random, circlePositions.length)];
        newGrid[row][col].color = blackColor;
      }
    }

    // Rule 2: Fix squares with too many adjacent squares
    // Sort by number of adjacent squares (descending) to fix the worst offenders first
    squaresWithTooManyAdjacent.sort((a, b) => {
      const [rowA, colA] = a;
      const [rowB, colB] = b;
      const countA = countAdjacentSquares(newGrid, rowA, colA);
      const countB = countAdjacentSquares(newGrid, rowB, colB);
      return countB - countA;
    });

    for (const [row, col] of squaresWithTooManyAdjacent) {
      // Check if this square still has too many adjacent squares
      // (it might have been fixed by changing an adjacent square)
      if (countAdjacentSquares(newGrid, row, col) > 2) {
        // Change this square to a non-square shape
        newGrid[row][col].shape = getRandomItem(
          random,
          BlockShapes.filter((shape) => shape.shape !== 'square'),
        );
      }
    }

    return newGrid;
  }

  // Generate a grid of random blocks
  const blockGrid = useMemo(() => {
    // Create a base random generator for the entire grid
    const baseRandom = createRandom(seed);

    // First, generate a grid with random shapes but without considering adjacency for colors
    let initialGrid = Array.from({ length: height }, (_, rowIndex) =>
      Array.from({ length: width }, (_, colIndex) => {
        // Generate a unique seed for this cell
        const cellSeed = generateCellSeed(seed, rowIndex, colIndex);

        // Create a random generator for this cell
        const random = createRandom(cellSeed);

        // Get random shape based on probabilities
        const randomShape = getRandomShape(random);

        // Initially assign a random color (avoid black for non-circles)
        let randomColor: (typeof BlockColors)[number];
        if (randomShape.shape === 'circle' && random() < 0.2) {
          randomColor = blackColor;
        } else {
          randomColor = getRandomItem(random, availableNonBlackColors);
        }

        return {
          color: randomColor,
          shape: randomShape,
        };
      }),
    );

    // Second pass: Apply color adjacency weights or ensure randomness
    const tempGrid = JSON.parse(
      JSON.stringify(initialGrid),
    ) as BlockConfiguration[][];

    for (let rowIndex = 0; rowIndex < height; rowIndex++) {
      for (let colIndex = 0; colIndex < width; colIndex++) {
        // Create a random generator for this cell
        const cellSeed = generateCellSeed(seed + 1, rowIndex, colIndex); // Use a different seed
        const random = createRandom(cellSeed);

        // Check adjacent cells that have already been processed
        const adjacentColors: (typeof BlockColors)[number][] = [];

        if (rowIndex > 0) {
          adjacentColors.push(tempGrid[rowIndex - 1][colIndex].color);
        }

        if (colIndex > 0) {
          adjacentColors.push(tempGrid[rowIndex][colIndex - 1].color);
        }

        // With some probability based on sameColorAdjacencyWeight,
        // use a color from adjacent cells
        if (adjacentColors.length > 0 && random() < sameColorAdjacencyWeight) {
          // Only use adjacent colors if they're valid for this shape
          const isCircle =
            tempGrid[rowIndex][colIndex].shape.shape === 'circle';

          // For non-circles, filter out black from adjacent colors
          const validAdjacentColors = isCircle
            ? adjacentColors
            : adjacentColors.filter((color) => color !== blackColor);

          if (validAdjacentColors.length > 0) {
            tempGrid[rowIndex][colIndex].color =
              validAdjacentColors[
                getRandomInt(random, validAdjacentColors.length)
              ];
          } else if (isCircle) {
            // For circles, we can use any color including black
            tempGrid[rowIndex][colIndex].color =
              random() < 0.2
                ? blackColor
                : getRandomItem(random, availableNonBlackColors);
          } else {
            // For non-circles, use any non-black color
            tempGrid[rowIndex][colIndex].color = getRandomItem(
              random,
              availableNonBlackColors,
            );
          }
        } else {
          // Otherwise, choose a random color that's different from adjacent colors if possible
          const isCircle =
            tempGrid[rowIndex][colIndex].shape.shape === 'circle';

          if (isCircle) {
            // For circles, we can use black or any other color
            const allColors = [blackColor, ...availableNonBlackColors];
            const nonAdjacentColors = allColors.filter(
              (color) => !adjacentColors.includes(color),
            );

            if (nonAdjacentColors.length > 0) {
              tempGrid[rowIndex][colIndex].color = getRandomItem(
                random,
                nonAdjacentColors,
              );
            } else {
              // If all colors are adjacent, just pick a random one
              tempGrid[rowIndex][colIndex].color =
                random() < 0.2
                  ? blackColor
                  : getRandomItem(random, availableNonBlackColors);
            }
          } else {
            // For non-circles, only use non-black colors
            const nonAdjacentColors = availableNonBlackColors.filter(
              (color) => !adjacentColors.includes(color),
            );

            if (nonAdjacentColors.length > 0) {
              tempGrid[rowIndex][colIndex].color = getRandomItem(
                random,
                nonAdjacentColors,
              );
            } else {
              // If all colors are adjacent, just pick a random non-black one
              tempGrid[rowIndex][colIndex].color = getRandomItem(
                random,
                availableNonBlackColors,
              );
            }
          }
        }
      }
    }

    initialGrid = tempGrid;

    // Then apply our rules to the grid
    return applyRules(initialGrid, baseRandom);
  }, [
    width,
    height,
    seed,
    circleProb,
    quarterCircleProb,
    rectangleProb,
    blackColor,
    availableNonBlackColors,
    sameColorAdjacencyWeight,
  ]);

  return blockGrid;
};
